import styled, { css } from 'styled-components';
import { size } from '../../layout/helpers';
import { Button } from '../Button';
import { Icon } from '../Icon';

interface TagWrapperProps {
  $active?: boolean;
  $size?: 'small' | 'tiny';
  $type?: 'rounded';
  $tone?: 'secondary';
  $isClickable: boolean;
}

export const TagWrapper = styled.div<TagWrapperProps>`
  border-radius: 5px;
  background-color: var(--c-bg-main);
  color: var(--c-text-main);
  font-weight: 400;
  border: 1px solid var(--c-border-main);
  white-space: nowrap;
  display: flex;
  align-items: center;
  width: fit-content;

  ${({ $isClickable }) =>
    $isClickable &&
    css`
      &:hover {
        color: var(--c-text-muted);
      }
    `};

  ${({ $active }) =>
    $active &&
    css`
      background-color: var(--c-contrast-bg);
      color: var(--c-contrast-fg);
      border: 1px solid var(--c-contrast-fg);

      ${Button} {
        color: var(--c-contrast-fg);

        &:hover {
          color: var(--c-contrast-bg);
        }
      }
    `};

  ${({ $active, $isClickable }) =>
    $active &&
    $isClickable &&
    css`
      &:hover {
        color: var(--c-bg-main);
        background-color: var(--c-semantic-neutral-700);
        button {
          color: var(--c-bg-main);
        }
      }
    `};

  ${({ $type }) =>
    $type === 'rounded' &&
    css`
      border-radius: 32px;
      background-color: var(--c-bg-main);
      color: var(--c-text-main);
      border: 1px solid var(--c-border-main);

      &:hover {
        color: var(--c-bg-main);
        background-color: var(--c-accent-bg);
        button {
          color: var(--c-text-main);
        }
      }

      ${Button} {
        color: var(--c-text-main);
      }
    `};

    ${({ $type, $tone }) =>
      $type === 'rounded' &&
      $tone === 'secondary' &&
      css`
      border-radius: 32px;
      background-color: var(--c-semantic-neutral-300);
      color: var(--c-text-main);
      border: none;

      &:hover {
        color: var(--c-bg-main);
        background-color: var(--c-accent-bg);
        button {
          color: var(--c-text-main);
        }
      }

      ${Button} {
        color: var(--c-text-main);
      }
    `};

  ${Button} {
    border-radius: 2px;

    &:first-child {
      padding-right: ${size(0.25)};
      font-weight: 400;
    }

    &:last-child {
      padding-left: ${size(0.5)};
      padding-right: ${size(1)};

      &:hover {
        svg {
          background-color: var(--c-semantic-neutral-300);
          border-radius: 50%;
        }
      }
    }

    &:only-child {
      ${({ $size }) =>
        $size === 'tiny' &&
        css`
          padding-left: 0.75rem;
          padding-right: 0.75rem;
        `}
      ${({ $size }) =>
        $size === 'small' &&
        css`
          padding-left: 1rem;
          padding-right: 1rem;
        `}
    }
  }

  ${Icon} {
    font-size: ${size(2.5)};
  }
`;

interface LabelProps {
  $size?: 'tiny' | 'small';
}

export const Label = styled.div<LabelProps>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: ${size(5.5)};
  padding: ${size(1.5)} ${size(4)};
  font-weight: var(--font-weight-bold);

  ${({ $size }) =>
    $size === 'tiny'
      ? css`
          min-height: 2rem;
          padding: 0.25rem 0.75rem;
          border-radius: 1rem;
          font-size: ${(props) => props.theme.font.size.xsmall};
        `
      : $size === 'small'
        ? css`
            min-height: 2.25rem;
            padding: 0.5rem 1rem;
            border-radius: 1.125rem;
          `
        : undefined}
`;

export const IconWrapper = styled.div`
  margin-right: ${size(0.5)};
`;
